import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    LayoutDashboard, Users, GitBranch, Mail, FileText, MessageSquare,
    Settings, LogOut, Eye, ChevronDown, ChevronRight, Newspaper,
    MessageCircle, Package, BarChart3, Mic2, BookOpen, Heart,
    ShoppingBag, Wallet, Star, Image, Send, Sparkles, Shield,
    Database, KeyRound, Clock, UserCog, HeartHandshake, Megaphone,
    ListChecks, BookMarked, Palette,
} from 'lucide-react';

// Sidebar usa cores fixas terrosas (não via variáveis CSS)
// bg sidebar:  #2D0C10  (Woods-profundo)
// ativo:       #8B3020 / text #E8C4A8 (Tostado escuro / Amendoa claro)
// hover:       #8B3020/25

const MenuItem = ({ item, isChild = false }) => {
    const location = useLocation();
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    return (
        <NavLink
            to={item.href}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all
                ${isActive
                    ? 'bg-[#8B3020]/50 text-[#E8C4A8] font-semibold'
                    : 'text-[#C49A80] hover:text-[#F0DECA] hover:bg-[#8B3020]/25'}
                ${isChild ? 'pl-9' : ''}`}
        >
            <item.icon className="w-4 h-4 flex-shrink-0" />
            <span>{item.name}</span>
        </NavLink>
    );
};

const MenuGroup = ({ group }) => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(() =>
        group.children.some(c => location.pathname.startsWith(c.href))
    );
    const hasActive = group.children.some(c => location.pathname.startsWith(c.href));

    return (
        <div className="space-y-0.5">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-all
                    ${hasActive ? 'text-[#E8C4A8]' : 'text-[#C49A80] hover:text-[#F0DECA] hover:bg-[#8B3020]/25'}`}
            >
                <div className="flex items-center gap-3">
                    <group.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{group.name}</span>
                </div>
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            {isOpen && (
                <div className="ml-3 border-l border-[#5C2015]/60 pl-2 space-y-0.5">
                    {group.children.map(item => (
                        <MenuItem key={item.href} item={item} isChild />
                    ))}
                </div>
            )}
        </div>
    );
};

const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    {
        name: 'CRM',
        icon: Users,
        isGroup: true,
        children: [
            { name: 'Leads',         href: '/leads',      icon: Users },
            { name: 'Lista de Espera', href: '/waitlist', icon: Clock },
            { name: 'Mensagens',     href: '/contacts',   icon: MessageSquare },
            { name: 'WhatsApp',      href: '/whatsapp',   icon: MessageCircle },
            { name: 'Atendimento',   href: '/eventos', icon: HeartHandshake },
        ],
    },
    {
        name: 'Automação',
        icon: GitBranch,
        isGroup: true,
        children: [
            { name: 'Fluxos',          href: '/flows',    icon: GitBranch },
            { name: 'Campanhas',       href: '/campaigns',icon: Megaphone },
            { name: 'Followup Rules',  href: '/followup', icon: ListChecks },
            { name: 'Templates Email', href: '/templates',icon: Mail },
        ],
    },
    { name: 'Eventos', href: '/eventos', icon: Star },
    {
        name: 'Conteúdo',
        icon: Newspaper,
        isGroup: true,
        children: [
            { name: 'Blog / CMS',       href: '/blog',       icon: FileText },
            { name: 'Galeria',          href: '/galeria',    icon: Image },
            { name: 'Testemunhos',      href: '/testemunhos',icon: Heart },
            { name: 'Gerador Roteiros', href: '/roteiros',   icon: Sparkles },
            { name: 'Brand Voice',      href: '/brand-voice',icon: Palette },
        ],
    },
    {
        name: 'Estoque (Livros)',
        icon: BookOpen,
        isGroup: true,
        children: [
            { name: 'Dashboard',       href: '/estoque',               icon: BarChart3 },
            { name: 'Produtos',        href: '/estoque/produtos',       icon: Package },
            { name: 'Entrada',         href: '/estoque/entrada',        icon: ShoppingBag },
            { name: 'Movimentações',   href: '/estoque/movimentacoes',  icon: BookMarked },
        ],
    },
    { name: 'Financeiro', href: '/financeiro', icon: Wallet },
    {
        name: 'Analytics',
        icon: BarChart3,
        isGroup: true,
        children: [
            { name: 'KPIs',       href: '/analytics', icon: BarChart3 },
            { name: 'Visitantes', href: '/visitors',  icon: Eye },
            { name: 'Heatmap',    href: '/heatmap',   icon: Mic2 },
            { name: 'Menções',    href: '/mencoes',   icon: Send },
        ],
    },
    {
        name: 'Configurações',
        icon: Settings,
        isGroup: true,
        children: [
            { name: 'Equipe',        href: '/equipe',    icon: UserCog },
            { name: 'Usuários',      href: '/usuarios',  icon: Shield },
            { name: 'API Keys',      href: '/api-keys',  icon: KeyRound },
            { name: 'Backups',       href: '/backups',   icon: Database },
            { name: 'Preferências',  href: '/settings',  icon: Settings },
        ],
    },
];

export const Sidebar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="w-64 flex flex-col" style={{ background: '#2D0C10' }}>
            {/* Logo */}
            <div className="p-5 border-b border-[#5C2015]/60">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center"
                         style={{ background: 'linear-gradient(135deg, #A0462D, #5C2015)' }}>
                        <Mic2 className="w-5 h-5 text-[#E8C4A8]" />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-[#F0DECA]">Tayane Larini</p>
                        <p className="text-xs text-[#C49A80]">Hub Oficial</p>
                    </div>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
                {navigation.map((item) =>
                    item.isGroup ? (
                        <MenuGroup key={item.name} group={item} />
                    ) : (
                        <MenuItem key={item.href} item={item} />
                    )
                )}
            </nav>

            {/* Usuário */}
            <div className="p-3 border-t border-[#5C2015]/60">
                <div className="flex items-center justify-between px-2 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                             style={{ background: '#8B3020' }}>
                            <span className="text-xs font-bold text-[#E8C4A8]">
                                {user?.name?.[0]?.toUpperCase() || 'A'}
                            </span>
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-medium text-[#F0DECA] truncate">{user?.name || 'Admin'}</p>
                            <p className="text-xs text-[#C49A80] truncate">{user?.role}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="p-1.5 rounded-lg text-[#C49A80] hover:text-red-400 hover:bg-[#5C2015]/50 transition-all"
                        title="Sair"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
